// Phase 6 — Track D T-D3. AlertRow.
//
// Per blueprint section 14.4: priority is conveyed via a surface wash + a
// border-accent on the left edge + a paired icon. Per section 13.5 the icon
// is non-decorative; an sr-only span announces the priority for screen
// readers, so colour is never the sole carrier of meaning.

import { Info, Bell, AlertTriangle, AlertOctagon } from 'lucide-react';
import type { AlertWithPinned, Priority } from '@dailydeck/shared/types';
import { copy } from '@dailydeck/shared/copy';
import { ListRow } from '../../components/ui/ListRow.js';
import { cn } from '../../lib/cn.js';

function priorityWashClass(priority: Priority): string {
  switch (priority) {
    case 'critical':
      return 'bg-priority-critical border-l-4 border-l-state-warning';
    case 'important':
      return 'bg-priority-important border-l-4 border-l-state-warning';
    case 'info':
    default:
      return '';
  }
}

function priorityIcon(priority: Priority): JSX.Element {
  switch (priority) {
    case 'critical':
      return <AlertOctagon size={18} strokeWidth={1.75} aria-hidden="true" />;
    case 'important':
      return <AlertTriangle size={18} strokeWidth={1.75} aria-hidden="true" />;
    case 'info':
    default:
      return <Info size={18} strokeWidth={1.75} aria-hidden="true" />;
  }
}

export interface AlertRowProps {
  alert: AlertWithPinned;
}

export function AlertRow({ alert }: AlertRowProps): JSX.Element {
  const wash = priorityWashClass(alert.priority);
  return (
    <ListRow
      className={cn('items-start gap-3', wash)}
      data-priority={alert.priority}
      data-alert-type={alert.alertTypeId}
    >
      <span className="mt-0.5 text-text-secondary" aria-hidden="true">
        {priorityIcon(alert.priority)}
      </span>
      <div className="flex-1 min-w-0">
        <p className={cn('text-body-strong text-text-primary', alert.priority === 'critical' && 'font-semibold')}>
          <span className="sr-only">{`${copy.alerts.priorities[alert.priority]} priority. `}</span>
          {alert.title}
        </p>
        {alert.body !== undefined && alert.body !== '' && (
          <p className="text-caption text-text-secondary mt-0.5">{alert.body}</p>
        )}
      </div>
      {alert.pinned && (
        <span
          aria-label="Pinned alert"
          className="ml-2 mt-1 inline-flex items-center justify-center rounded-full bg-accent-subtle px-2 py-0.5 text-micro text-accent-pressed"
        >
          <Bell size={12} strokeWidth={1.75} aria-hidden="true" className="mr-1" />
          Pinned
        </span>
      )}
    </ListRow>
  );
}
